import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, MapPin, Users, MessageSquare, Search, IndianRupee } from "lucide-react";
import Navbar from "@/components/app/Navbar";

const POPULAR_CITIES = [
  { city: "Mumbai", locality: "Bandra West" },
  { city: "Bangalore", locality: "Koramangala" },
  { city: "Delhi", locality: "Hauz Khas" },
  { city: "Pune", locality: "Viman Nagar" },
  { city: "Hyderabad", locality: "Gachibowli" },
  { city: "London", locality: "Shoreditch" },
  { city: "New York", locality: "Manhattan" },
  { city: "San Francisco", locality: "Mission" },
];
const HERO = "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1600";

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="chip chip-verified mb-6"><ShieldCheck className="w-3 h-3" /> Broker-free · Global Community</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-stone-900 leading-[1.05] tracking-tight">
              Find your flock.<br />Find a home.
            </h1>
            <p className="mt-6 text-lg text-stone-600 leading-relaxed max-w-lg">
              Rentals and flatmates in top cities worldwide — verified owners, granular filters, and masked chat that opens only when both sides say yes.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-cta-browse" className="brand-accent rounded-full text-base px-7 py-6" onClick={() => nav("/browse")}>
                <Search className="w-4 h-4 mr-2" /> Browse rentals
              </Button>
              <Button data-testid="hero-cta-post" variant="outline" className="rounded-full text-base px-7 py-6 border-stone-300" onClick={() => nav("/post")}>
                Post a listing
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-stone-500">
              <div><span className="text-stone-900 font-semibold">0%</span> broker fee</div>
              <div><span className="text-stone-900 font-semibold">100%</span> verified listings</div>
              <div><span className="text-stone-900 font-semibold">Verified</span> profiles</div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative rounded-3xl overflow-hidden border border-stone-200 shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
              <img loading="lazy" src={HERO} alt="Modern apartment" className="w-full aspect-[4/3] object-cover" />
              <div className="absolute inset-0 hero-mask" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div className="text-white">
                  <div className="text-xs uppercase tracking-[0.2em] opacity-80">Featured</div>
                  <div className="font-display text-2xl font-medium">Bandra West · 2BHK</div>
                </div>
                <div className="chip bg-white/95 text-stone-900 flex items-center gap-1"><IndianRupee className="w-3 h-3" />28,000/mo</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-3">How it works</div>
            <h2 className="font-display text-3xl md:text-4xl font-medium text-stone-900">Three steps, zero brokers.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { icon: Search, title: "Filter your way", body: "Locality, budget, veg/non-veg, gender, occupation. Filters that actually matter to you." },
              { icon: Users, title: "Send an enquiry", body: "Owners see a queue of interested renters. They accept or reject — you're never ghosted." },
              { icon: MessageSquare, title: "Chat unlocks", body: "Masked in-app chat opens post-match. No contact info leaks until both sides opt in." },
            ].map((s) => (
              <div key={s.title} className="p-8 bg-[#FDFBF7] border border-stone-200 rounded-2xl">
                <div className="w-11 h-11 rounded-xl brand-primary grid place-items-center mb-5">
                  <s.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-display text-xl font-medium text-stone-900">{s.title}</h3>
                <p className="mt-2 text-stone-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Localities */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-3">Explore</div>
              <h2 className="font-display text-3xl md:text-4xl font-medium text-stone-900">Popular cities & neighborhoods</h2>
            </div>
            <Button variant="ghost" className="text-brand" data-testid="see-all-localities" onClick={() => nav("/browse")}>
              See all →
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {POPULAR_CITIES.map((item) => (
              <button
                key={`${item.city}-${item.locality}`}
                data-testid={`locality-${item.city.toLowerCase().replace(/\s+/g,'-')}`}
                onClick={() => nav(`/browse?city=${encodeURIComponent(item.city)}`)}
                className="group relative overflow-hidden rounded-2xl border border-stone-200 bg-white p-5 text-left hover:border-[#2A4B3C]/40 transition"
              >
                <MapPin className="w-4 h-4 text-brand mb-2" />
                <div className="font-display font-medium text-stone-900">{item.locality}</div>
                <div className="text-xs text-stone-500 mt-1">{item.city}</div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <footer className="py-10 border-t border-stone-200 text-center text-sm text-stone-500">
        © {new Date().getFullYear()} Flock · Made for renters, not brokers.
      </footer>
    </div>
  );
}
